import { Component, OnInit, Inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { ObjectHelpers, SabisNgxDialogComponent, SabisButton2Component, SabisComboBox2Component } from 'sabis-components2';
import { AppLoaderService } from '../../../@theme/shared/services/app-loader/app-loader.service';
import { AppNotificationsService } from '../../../core/notifications/app-notifications.service';
import { toLookupItems } from '../../../shared/api-common-models';
import { LookupItem } from '../../../shared/lookup/models';
import { ApplicationFeedbackType, SaveStepFeedbackPermissionsDTO } from '../models/api-models';
import { RecruitmentService } from '../recruitment.service';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'sca-permissions-editor',
  templateUrl: './permissions-editor.component.html',
  styleUrls: ['./permissions-editor.component.scss'],
  imports: [
    FormsModule,
    MatCardModule,
    MatRadioModule,
    MatDividerModule,
    SabisButton2Component,
    SabisNgxDialogComponent,
    SabisComboBox2Component
  ]
})
export class PermissionsEditorComponent implements OnInit {
  public isLoading: boolean = true;
  public isSaving: boolean = false;

  // recruiters
  public allUsers: LookupItem<any>[] = [];
  public selectedAllowedToRecruiterIds: number[] = [];
  public selectedAccessOption: number = 0;


  constructor(private _recruitmentService: RecruitmentService,
    public dialogRef: MatDialogRef<PermissionsEditorComponent>,
    private _appNotification: AppNotificationsService,
    private _appLoader: AppLoaderService,
    @Inject(MAT_DIALOG_DATA) public data: PermissionsDialogData) {
  }

  ngOnInit(): void {
    this.loadFeedbackPermissions();
  }

  private loadFeedbackPermissions(): void {
    this.isLoading = true;

    this._recruitmentService.getStepFeedbackPermissionsInitData(this.data.applicationId, this.data.selectionStepId, this.data.applicationFeedbackType).subscribe(r => {
      this.selectedAllowedToRecruiterIds = r.allowedToUserIds;
      this.allUsers = toLookupItems(r.allUsers);
      if (this.selectedAllowedToRecruiterIds.length > 0) {
        this.selectedAccessOption = 1;
      }

      this.isLoading = false;
      this._appLoader.hideLoader();
    });
  }

  public onCancelButtonClick(): void {
    this.dialogRef.close();
  }

  /**
   * @returns true if valid
   */
  private validateForSave(): boolean {
    // cannot select same recruiter in both allowedRecruiters and restrictedRecruiters
    let selectedIds: number[] = [];
    selectedIds.push(...this.selectedAllowedToRecruiterIds);

    let hasDuplicatedUsers = ObjectHelpers.hasDuplicates(selectedIds);
    if (hasDuplicatedUsers) {
      this._appNotification.sameProfileSelectedMessage("user");
      return false;
    }

    return true;
  }

  public onSaveButtonClick(): void {
    if (this.isSaving) {
      return;
    }

    let isValid = this.validateForSave();
    if (!isValid) {
      this._appNotification.showInvalidEntriesMessage();
      return;
    }

    this.isSaving = true;

    let command: SaveStepFeedbackPermissionsDTO = {
      selectionStepId: this.data.selectionStepId,
      applicationId: this.data.applicationId,
      applicationFeedbackType: this.data.applicationFeedbackType,
      allowedToUserIds: this.selectedAccessOption === 1 ? this.selectedAllowedToRecruiterIds : []
    };

    this._appLoader.showSaving();

    let request = this._recruitmentService.saveFeedbackPermission(command);

    request.pipe(
      finalize(() => {
        this.isSaving = false;
        this._appLoader.hideSaving();
      })
    ).subscribe({
      next: (r) => {
        this._appNotification.notifySaveSucceeded();
        this.dialogRef.close(true);
      },
      error: () => {
        this._appNotification.notifySaveFailed();
      }
    });
  }
}

export interface PermissionsDialogData {
  applicationId: number;
  selectionStepId: number;
  applicationFeedbackType: ApplicationFeedbackType
}
